import { useEffect, useRef, useState } from "react";
import * as React from "react";
import PropTypes from "prop-types";
import { Grid, Typography, IconButton, Button } from "@mui/material";
import Box from "@mui/material/Box";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Line, Doughnut } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { GetLineGraphicOptions, GraphicNoDataImg } from "../constants/graphics";
import ChartPlaceholder from "./chartPlaceholder";

// Envío sms por producto: alterna entre la serie diaria por producto
// y la distribución de campañas normales / personalizadas
const ProductSendChart = ({
  classes,
  darkMode,
  loading,
  noData,
  dateRange,
  linearGraphic,
  campaignTypeGraphic,
  lineGraphicExternalLegend,
  totalCampaignType,
}) => {
  const legendMenuRef = useRef(null);
  const [openLegend, setOpenLegend] = useState(false);
  const [showCampaignTypeChart, setShowCampaignTypeChart] = useState(false);

  useEffect(() => {
    function handleClickOutside(event) {
      if (
        legendMenuRef.current &&
        !legendMenuRef.current.contains(event.target)
      ) {
        setOpenLegend(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [legendMenuRef]);

  const getOrCreateLegendList = (id) => {
    const legendContainer = document.getElementById(id);
    let listContainer = legendContainer.querySelector("ul");

    if (!listContainer) {
      listContainer = document.createElement("ul");
      listContainer.style.display = "flex";
      listContainer.style.flexDirection = "column";
      listContainer.style.margin = 0;
      listContainer.style.padding = 0;

      legendContainer.appendChild(listContainer);
    }

    return listContainer;
  };

  const handleOption = (lineId) => {
    const id = document.getElementById(lineId);
    id.click();
  };

  const htmlLegendPlugin = {
    id: "htmlLegend",
    afterUpdate(chart) {
      const ul = getOrCreateLegendList("js-legend");
      while (ul.firstChild) {
        ul.firstChild.remove();
      }
      const items = chart.options.plugins.legend.labels.generateLabels(chart);
      items.forEach((item, index) => {
        const id = `li-${index}`;

        const li = document.createElement("li");
        li.style.display = "none";
        li.id = id;

        li.onclick = () => {
          chart.setDatasetVisibility(
            item.datasetIndex,
            !chart.isDatasetVisible(item.datasetIndex),
          );
          chart.update();
        };
        ul.appendChild(li);
      });
    },
  };

  const getToggleButtonStyles = (active) => ({
    fontFamily: "Ubuntu !important",
    textTransform: "none",
    backgroundColor: active ? "#1BC3DD" : "transparent",
    color: active ? "#FFFFFF" : darkMode ? "#FFFFFF" : "#171D1E",
    border: "1px solid " + (active ? "#1BC3DD" : "#E0E0E0"),
    borderRadius: "10px",
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: "500",
    minWidth: "auto",
    "&:hover": {
      backgroundColor: active
        ? "#00ACC1"
        : darkMode
          ? "rgba(255,255,255,0.1)"
          : "rgba(78,85,137,0.1)",
    },
  });

  return (
    <Grid
      item
      className={classes.graphicLinearContainer}
      style={{
        border:
          "1px solid " + (darkMode ? "rgba(224, 224, 224, 0.50)" : "#E0E0E0"),
        boxShadow: !darkMode ? "" : "0 2px 8px 0 rgba(255, 255, 255, 0.10)",
      }}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        height: "100%",
        minHeight: "100%",
        maxHeight: "100%",
      }}
    >
      <Grid
        item
        container
        direction={"column"}
        sx={{
          height: "100%",
          width: "100%",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          // Sin wrap: con wrap la columna toma el ancho del canvas y no encoge al redimensionar
          flexWrap: "nowrap",
        }}
      >
        {/* Header - Title and Buttons - Always at top */}
        <Grid
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          <Grid sx={{ display: "flex", flexDirection: "column", gap: "3px" }}>
            <Typography
              sx={{
                fontWeight: "bold",
                fontSize: 24,
                fontFamily: "Ubuntu !important",
              }}
            >
              {showCampaignTypeChart
                ? "Campañas normales/personalizadas"
                : "Envío sms por producto"}
            </Typography>

            <Typography
              sx={{
                fontWeight: 300,
                fontSize: "16px",
                color: darkMode ? "white" : "#637381",
                fontFamily: "Public Sans !important",
              }}
            >
              {showCampaignTypeChart
                ? "Relación entre tipos de campañas"
                : "Volumen de envio de mensajes"}
            </Typography>
          </Grid>
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Button
              onClick={() => setShowCampaignTypeChart(false)}
              sx={getToggleButtonStyles(!showCampaignTypeChart)}
            >
              Envío SMS
            </Button>
            <Button
              onClick={() => setShowCampaignTypeChart(true)}
              sx={getToggleButtonStyles(showCampaignTypeChart)}
            >
              Campañas normales/personalizadas
            </Button>
          </Box>
        </Grid>
        {/* Chart Content Area - Fills remaining space */}
        <Grid
          sx={{
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            marginTop: "20px",
          }}
        >
          {loading === false && linearGraphic && !noData ? (
            showCampaignTypeChart ? (
              // Campaign Type Circular Chart
              <Grid
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                }}
              >
                {campaignTypeGraphic && (
                  <>
                    {/* Chart centered in available space */}
                    <Grid
                      sx={{
                        flex: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 0,
                      }}
                    >
                      <Grid sx={{ width: "100%", maxWidth: "320px" }}>
                        {totalCampaignType?.normal > 0 ||
                        totalCampaignType?.custom > 0 ? (
                          <Doughnut
                            data={campaignTypeGraphic}
                            options={{
                              responsive: true,
                              maintainAspectRatio: true,
                              aspectRatio: 1.2,
                              radius: "73%",
                              cutout: "65%",
                              spacing: 0,
                              interaction: {
                                mode: null,
                              },
                              plugins: {
                                legend: {
                                  display: false,
                                },
                                tooltip: {
                                  enabled: false,
                                },
                                datalabels: {
                                  labels: {
                                    index: {
                                      align: "end",
                                      anchor: "end",
                                      color: function (context) {
                                        return context.dataset.backgroundColor[
                                          context.dataIndex
                                        ];
                                      },
                                      font: {
                                        size: 18,
                                        weight: "bold",
                                        family: "Ubuntu",
                                      },
                                      formatter: function (value) {
                                        const total =
                                          (totalCampaignType?.normal || 0) +
                                          (totalCampaignType?.custom || 0);
                                        if (total === 0) return "0%";
                                        return `${((value * 100) / total).toFixed(1)}%`;
                                      },
                                      offset: 8,
                                    },
                                  },
                                },
                              },
                            }}
                            plugins={[ChartDataLabels]}
                          />
                        ) : (
                          <ChartPlaceholder
                            darkMode={darkMode}
                            image={
                              darkMode
                                ? GraphicNoDataImg.NoDataLineDark
                                : GraphicNoDataImg.NoDataLineWhite
                            }
                          />
                        )}
                      </Grid>
                    </Grid>
                    {/* Legend always at bottom */}
                    <Grid
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "40px",
                        paddingY: "16px",
                        flexShrink: 0,
                      }}
                    >
                      {campaignTypeGraphic.labels.map((label, i) => (
                        <Grid
                          key={i}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Box
                            sx={{
                              width: "14px",
                              height: "24px",
                              backgroundColor:
                                campaignTypeGraphic.datasets[0].backgroundColor[
                                  i
                                ],
                              borderRadius: "30px",
                            }}
                          />
                          <Typography
                            sx={{
                              fontFamily: "Ubuntu",
                              fontSize: "16px",
                              fontWeight: "400",
                              color:
                                campaignTypeGraphic.datasets[0].backgroundColor[
                                  i
                                ],
                            }}
                          >
                            {label}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </>
                )}
              </Grid>
            ) : (
              // Original Line Chart
              <Grid
                sx={{
                  width: "100%",
                  marginTop: "20px",
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* El canvas se estira al alto disponible en vez de imponer
                    su relación de aspecto, que es lo que alargaba la tarjeta */}
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    minWidth: 0,
                    width: "100%",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  <Line
                    id="lines"
                    options={{
                      ...GetLineGraphicOptions(
                        darkMode,
                        dateRange,
                        linearGraphic,
                      ),
                      maintainAspectRatio: false,
                    }}
                    data={linearGraphic}
                    plugins={[htmlLegendPlugin]}
                  />
                </Box>
                {/* Legend at bottom */}
                <Grid
                  item
                  sx={{
                    display: "flex",
                    justifyContent: "end",
                    flexShrink: 0,
                  }}
                >
                  <Grid ref={legendMenuRef} sx={{ position: "relative" }}>
                    <IconButton
                      type="button"
                      id="menu"
                      onClick={() => setOpenLegend(!openLegend)}
                      sx={{
                        color: darkMode ? "white" : "#4E5589",
                        transform: "translateY(0px)",
                      }}
                    >
                      <KeyboardArrowDownIcon />
                    </IconButton>

                    <Grid
                      id="js-legend"
                      sx={{ display: openLegend ? "inline" : "none" }}
                      className={"legend-css " + classes.legendMenu}
                    >
                      <FormGroup>
                        {lineGraphicExternalLegend.map((line, index) => {
                          return (
                            <Grid
                              key={index}
                              item
                              container
                              direction={"row"}
                              alignItems={"flex-end"}
                              sx={{ width: "100%" }}
                            >
                              <FormControlLabel
                                sx={{
                                  "& .MuiTypography-root ": {
                                    fontSize: "13px !important",
                                    fontFamily: "Ubuntu !important",
                                    marginLeft: "3px",
                                  },
                                }}
                                control={
                                  <Checkbox
                                    onClick={() => {
                                      handleOption(`li-${line.id}`);
                                    }}
                                    sx={{
                                      paddingRight: "3px !important",
                                      paddingBottom: "2px !important",
                                      paddingTop: "2px !important",
                                      color: line.color,
                                      "&.Mui-checked": {
                                        color: line.color,
                                      },

                                      "& .MuiSvgIcon-root": {
                                        fontSize: 16,
                                      },
                                    }}
                                    defaultChecked
                                  />
                                }
                                label={line.label}
                              />
                            </Grid>
                          );
                        })}
                      </FormGroup>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )
          ) : (
            <ChartPlaceholder
              darkMode={darkMode}
              loading={loading}
              image={
                darkMode
                  ? GraphicNoDataImg.NoDataLineDark
                  : GraphicNoDataImg.NoDataLineWhite
              }
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

ProductSendChart.propTypes = {
  classes: PropTypes.object.isRequired,
  darkMode: PropTypes.bool.isRequired,
  loading: PropTypes.bool.isRequired,
  noData: PropTypes.bool.isRequired,
  dateRange: PropTypes.object.isRequired,
  linearGraphic: PropTypes.object,
  campaignTypeGraphic: PropTypes.object,
  lineGraphicExternalLegend: PropTypes.array.isRequired,
  totalCampaignType: PropTypes.object,
};

export default ProductSendChart;
